import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { SKILLS_DATA } from '../../lib/constants';

type SkillCategory = 'languages' | 'frameworks' | 'aiBuilders' | 'databases' | 'marketing';

const categories: { key: SkillCategory; label: string }[] = [
  { key: 'languages', label: 'Bahasa Pemrograman' },
  { key: 'frameworks', label: 'Framework' },
  { key: 'aiBuilders', label: 'AI Builders' },
  { key: 'databases', label: 'Database & Cloud' },
  { key: 'marketing', label: 'Marketing & Web3' },
];

export default function SkillsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeCategory, setActiveCategory] = useState<SkillCategory>('languages');

  return (
    <section id="skills" ref={ref} className="py-20 bg-dark-lighter/30">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="neon-text-blue">Keahlian</span> <span className="text-white">Saya</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-blue to-neon-green mx-auto rounded-full"></div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setActiveCategory(category.key)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeCategory === category.key
                  ? 'bg-neon-blue text-white shadow-[0_0_20px_rgba(14,165,233,0.8)]'
                  : 'glass-card text-gray-300 hover:text-neon-blue'
              }`}
            >
              {category.label}
            </button>
          ))}
        </motion.div>

        {/* Skills Grid */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {SKILLS_DATA[activeCategory].map((skill, index) => (
            <motion.div
              key={skill.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="glass-card p-6"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-white">{skill.name}</h3>
                <span className="text-neon-blue font-bold">{skill.level}%</span>
              </div>
              <div className="w-full bg-dark-lighter rounded-full h-3 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${skill.level}%` }}
                  transition={{ duration: 1, delay: 0.2 + index * 0.1 }}
                  className="h-full bg-gradient-to-r from-neon-blue to-neon-green rounded-full relative"
                  style={{
                    boxShadow: '0 0 10px rgba(14, 165, 233, 0.8)',
                  }}
                >
                  <div className="absolute inset-0 animate-pulse bg-white/20 rounded-full"></div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
